import { createThemeToggleButton } from '../ui/theme';
import { createLangToggleButton, t } from '../ui/lang';
// ── Shared card shell ─────────────────────────────────────────────────────────
function renderCard(content) {
    document.body.innerHTML = `
        <div class="login-wrap">
            <div class="login-card">
                <div class="login-brand-wrap">
                    <div class="login-brand-icon">📱</div>
                </div>
                ${content}
            </div>
        </div>
    `;
    const card = document.querySelector('.login-card');
    if (card) {
        const toggle = createThemeToggleButton('login-theme-toggle');
        card.insertBefore(toggle, card.firstChild);
        // Before the panel exists there is nowhere else to switch language, and the login
        // form is the first thing a Russian-speaking operator reads.
        card.insertBefore(createLangToggleButton('login-lang-toggle'), card.firstChild);
    }
}
// ── Step 1: login + password ───────────────────────────────────────────────────
export function renderLogin(onSuccess) {
    renderCard(`
        <h1 class="login-title">Mobile Farm</h1>
        <p class="login-subtitle">${t('auth.sign_in_subtitle')}</p>
        <form id="login-form" class="login-form" novalidate autocomplete="off">
            <div class="login-field">
                <label for="login-input">${t('auth.login_label')}</label>
                <input id="login-input" type="text" autocomplete="off" required placeholder="${t('auth.login_placeholder')}" />
            </div>
            <div class="login-field">
                <label for="password-input">${t('auth.password_label')}</label>
                <input id="password-input" type="password" autocomplete="off" required placeholder="${t('auth.password_placeholder')}" />
            </div>
            <div id="login-error" class="login-error" style="display:none"></div>
            <button type="submit" class="login-btn">${t('auth.continue')}</button>
        </form>
    `);
    const form = document.getElementById('login-form');
    const loginEl = document.getElementById('login-input');
    const passEl = document.getElementById('password-input');
    const errorEl = document.getElementById('login-error');
    const btn = form.querySelector('button[type=submit]');
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.style.display = 'none';
        btn.disabled = true;
        btn.textContent = t('auth.checking');
        try {
            const res = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ login: loginEl.value, password: passEl.value }),
            });
            const data = await res.json();
            if (!res.ok || 'error' in data) {
                errorEl.textContent = ('error' in data ? data.error : null) ?? t('auth.login_failed');
                errorEl.style.display = 'block';
                return;
            }
            if (data.status === 'totp_setup') {
                renderTotpSetup(data.token, data.qr, onSuccess);
            }
            else {
                renderTotpVerify(data.token, onSuccess);
            }
        }
        catch {
            errorEl.textContent = t('auth.network_error');
            errorEl.style.display = 'block';
        }
        finally {
            btn.disabled = false;
            btn.textContent = t('auth.continue');
        }
    });
}
// ── Step 2a: first time — scan QR + confirm ───────────────────────────────────
function renderTotpSetup(token, qr, onSuccess) {
    renderCard(`
        <h1 class="login-title">${t('auth.totp_setup_title')}</h1>
        <p class="login-subtitle">${t('auth.totp_setup_subtitle')}</p>
        <img src="${qr}" class="totp-qr" alt="${t('auth.qr_alt')}" />
        <form id="totp-form" class="login-form" novalidate>
            <div class="login-field">
                <label for="totp-input">${t('auth.code_label')}</label>
                <input id="totp-input" type="text" inputmode="numeric" pattern="[0-9]{6}"
                       autocomplete="one-time-code" maxlength="6" placeholder="000000" required />
            </div>
            <div id="totp-error" class="login-error" style="display:none"></div>
            <button type="submit" class="login-btn">${t('auth.confirm_sign_in')}</button>
        </form>
    `);
    setupTotpForm(token, onSuccess);
}
// ── Step 2b: subsequent logins — enter code ───────────────────────────────────
function renderTotpVerify(token, onSuccess) {
    renderCard(`
        <h1 class="login-title">${t('auth.totp_verify_title')}</h1>
        <p class="login-subtitle">${t('auth.totp_verify_subtitle')}</p>
        <form id="totp-form" class="login-form" novalidate>
            <div class="login-field">
                <label for="totp-input">${t('auth.code_label')}</label>
                <input id="totp-input" type="text" inputmode="numeric" pattern="[0-9]{6}"
                       autocomplete="one-time-code" maxlength="6" placeholder="000000" required />
            </div>
            <div id="totp-error" class="login-error" style="display:none"></div>
            <button type="submit" class="login-btn">${t('auth.sign_in')}</button>
        </form>
    `);
    setupTotpForm(token, onSuccess);
}
// ── Shared TOTP form logic ────────────────────────────────────────────────────
function setupTotpForm(token, onSuccess) {
    const form = document.getElementById('totp-form');
    const codeEl = document.getElementById('totp-input');
    const errorEl = document.getElementById('totp-error');
    const btn = form.querySelector('button[type=submit]');
    codeEl.focus();
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.style.display = 'none';
        btn.disabled = true;
        btn.textContent = t('auth.verifying');
        try {
            const res = await fetch('/api/auth/totp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, code: codeEl.value }),
            });
            const data = await res.json();
            if (res.ok && 'login' in data) {
                onSuccess({ login: data.login, role: data.role });
            }
            else {
                errorEl.textContent = ('error' in data ? data.error : null) ?? t('auth.invalid_code');
                errorEl.style.display = 'block';
                codeEl.value = '';
                codeEl.focus();
            }
        }
        catch {
            errorEl.textContent = t('auth.network_error');
            errorEl.style.display = 'block';
        }
        finally {
            btn.disabled = false;
            btn.textContent = btn.textContent.includes(t('auth.verifying')) ? t('auth.sign_in') : btn.textContent;
        }
    });
}
